import { getPipingTable } from "./sqliteApi";
async function getSizesInRange(range, component, spec) {
  // Build the piping ranges table name
  const pipingRangesTableName = spec ? `piping_ranges_${spec}` : null;

  if (!pipingRangesTableName) {
    console.warn(
      `[getSizesInRange] Cannot generate piping ranges table name - missing Spec`,
    );
    return [];
  }

  // console.log(`[getSizesInRange] Looking up ranges in: ${pipingRangesTableName} for component: ${component}`);

  // Get the piping ranges table from allTables
  const pipingRangesTable = await getPipingTable(pipingRangesTableName);
  if (!pipingRangesTable || !pipingRangesTable.length) {
    console.warn(
      `[getSizesInRange] Piping ranges table not found: ${pipingRangesTableName}`,
    );
    // console.log("Available tables:", Object.keys(allTables).filter(k => k.includes('piping_ranges')));
    return [];
  }

  // Find the component row in the ranges table
  let rangesRow = pipingRangesTable.find((row) => row.Component === component);

  // If component not found, try to find "Other"
  if (!rangesRow) {
    console.warn(
      `[getSizesInRange] Component "${component}" not found, looking for "Other"`,
    );
    rangesRow = pipingRangesTable.find((row) => row.Component === "Other");
  }

  if (!rangesRow) {
    console.warn(
      `[getSizesInRange] Neither "${component}" nor "Other" found in ${pipingRangesTableName}`,
    );
    // console.log("Available components:", pipingRangesTable.map(r => r.Component));
    return [];
  }

  // Get the range from the database
  let targetSizes = rangesRow.Range;

  // ✅ UPDATED: Parse JSON string if it's a string, with cleaning
  if (typeof targetSizes === "string") {
    try {
      // Remove trailing semicolon and trim whitespace
      const cleanedString = targetSizes.trim().replace(/;+$/, "");
      targetSizes = JSON.parse(cleanedString);
      // console.log(`[getSizesInRange] Parsed Range string to array for ${component}`);
    } catch (error) {
      console.error(
        `[getSizesInRange] Failed to parse Range JSON for component "${component}":`,
        error,
      );
      console.error(`[getSizesInRange] Raw Range value:`, rangesRow.Range);
      return [];
    }
  }

  // Validate that Range is an array
  if (!Array.isArray(targetSizes)) {
    console.warn(
      `[getSizesInRange] Range for component "${component}" is not an array after parsing:`,
      targetSizes,
    );
    return [];
  }

  // console.log(`[getSizesInRange] Found range for ${component}:`, targetSizes);

  // Handle "all sizes" case - return entire range from database
  if (!range || range.trim().toLowerCase() === "all sizes") {
    // console.log(`[getSizesInRange] Range is "all sizes", returning entire range from database`);
    return [...targetSizes]; // Return all sizes from the database range
  }

  // Parse the range string (e.g., "1/2--4")
  const cleaned = range.trim();
  const [start, end] = cleaned.split("--").map((s) => s.trim());

  const startIndex = targetSizes.indexOf(start);
  const endIndex = targetSizes.indexOf(end);

  if (startIndex === -1 || endIndex === -1) {
    console.warn(
      `[getSizesInRange] Invalid size range: ${range}. Start="${start}" (index: ${startIndex}), End="${end}" (index: ${endIndex})`,
    );
    // console.log("Available sizes in range:", targetSizes);
    return [];
  }

  const minIndex = Math.min(startIndex, endIndex);
  const maxIndex = Math.max(startIndex, endIndex);

  const result = targetSizes.slice(minIndex, maxIndex + 1);
  // console.log(`[getSizesInRange] Returning sizes from ${start} to ${end}:`, result);

  return result;
}

export async function getSizesOptions(SelectedParts, spec, component) {
  if (!spec || !component) return [];

  const filteredSpecs = SelectedParts.filter(
    (cur) => cur.Spec === spec && cur.Components === component,
  );

  const res = [];
  const seenRanges = new Set();

  for (const cur of filteredSpecs) {
    try {
      const range = cur.Size;

      if (seenRanges.has(range)) continue;
      seenRanges.add(range);

      const sizesInRange = await getSizesInRange(
        range,
        cur.Components,
        spec,
      );

      if (!Array.isArray(sizesInRange)) {
        console.warn(
          "[getSizesOptions] getSizesInRange did not return array:",
          sizesInRange,
        );
        continue;
      }

      res.push(
        ...sizesInRange.map((size) => ({
          value: size,
          range,
        })),
      );
    } catch (e) {
      console.error("[getSizesOptions] Error:", e);
    }
  }

  return res;
}

export async function getComponentTab(component, spec) {
  // Build piping families table name
  const pipingFamiliesTableName = spec ? `piping_families_${spec}` : null;

  if (!pipingFamiliesTableName) {
    console.warn(
      `[getComponentTab] Cannot generate piping families table name - missing Spec`
    );
    return null;
  }

  // Get the piping families table
  const pipingFamiliesTable = await getPipingTable(pipingFamiliesTableName);

  console.log("pipingFamiliesTable", pipingFamiliesTable);

  if (!Array.isArray(pipingFamiliesTable) || pipingFamiliesTable.length === 0) {
    console.warn(
      `[getComponentTab] Piping families table not found: ${pipingFamiliesTableName}`
    );
    return null;
  }

  // Find the component row
  const componentRow = pipingFamiliesTable.find(
    (row) => String(row.Component || "").trim() === String(component || "").trim()
  );

  if (!componentRow) {
    console.warn(
      `[getComponentTab] Component "${component}" not found in ${pipingFamiliesTableName}`
    );
    return null;
  }

  // Get the Parts_Lib_Tab value
  const tabName = componentRow.Parts_Lib_Tab;

  if (!tabName) {
    console.warn(
      `[getComponentTab] Parts_Lib_Tab is empty for component "${component}"`
    );
    return null;
  }

  return tabName;
}

export function normalizeSize(input) {
  return input
    .trim()
    .replace(/^\((.*)\)$/, "$1")
    .replace(/\s+/g, "");
}
